import React, { useState } from 'react';
import { useQuery } from 'react-query';

import { DatePicker, Form, Upload } from 'antd';
import dayjs from 'dayjs';
import styled, { createGlobalStyle } from 'styled-components';

import { createShopRequestCommunicationLog } from 'apis/ActivitiesAPI';
import { getShopRequestShopperEmails } from 'apis/ShopRequestAPI';
import { useRootContext } from 'components/layout/RootContext';
import Button from 'components/lib/Button';
import Modal from 'components/lib/Modal';
import Select from 'components/lib/Select';
import TextArea from 'components/lib/TextArea';

import {
  SHOP_REQUEST_COMMUNICATION_LOG_ACTIVITY_TYPE,
  SHOP_REQUEST_COMMUNICATION_LOG_ACTIVITY_TYPE_OPTIONS,
  SHOP_REQUEST_COMMUNICATION_LOG_DIRECTION,
  SHOP_REQUEST_COMMUNICATION_LOG_DIRECTION_OPTIONS_BY_ACTIVITY_TYPE,
  SHOP_REQUEST_COMMUNICATION_LOG_OUTCOME,
  SHOP_REQUEST_COMMUNICATION_LOG_OUTCOME_OPTIONS_BY_ACTIVITY_TYPE_AND_DIRECTION,
  SHOP_REQUEST_TYPE,
} from '../constants';
import {
  ShopRequestCommunicationLogActivityType,
  ShopRequestCommunicationLogDirectionType,
  ShopRequestShopperEmailType,
  ShopRequestType,
} from '../types';
import EditShopRequestCommunicationLogUploadField from './EditShopRequestCommunicationLogUploadField';
import {
  buildCommunicationLogFilePayload,
  getFileSizeLimitErrorMessage,
  getOversizedUploadFile,
  isFileWithinSizeLimit,
} from './helpers';

const DEFAULT_FORM_VALUES = {
  activity_type: SHOP_REQUEST_COMMUNICATION_LOG_ACTIVITY_TYPE.PHONE_CALL,
  direction_type: SHOP_REQUEST_COMMUNICATION_LOG_DIRECTION.OUTBOUND,
  outcome: SHOP_REQUEST_COMMUNICATION_LOG_OUTCOME.NO_ANSWER,
  description: '',
  files: [],
};

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  column-gap: 12px;
  width: min(720px, calc(100vw - 48px));
  padding: 16px 14px 14px;

  .ant-form-item {
    margin-bottom: 12px;
  }

  .ant-form-item.full-width {
    grid-column: 1 / -1;
  }

  .ant-form-item-label {
    padding-bottom: 6px;
  }

  .ant-form-item-label > label {
    color: var(--text-tertiary);
    font-family: var(--font-bold);
    font-size: 14px;
  }

  @media (max-width: 620px) {
    grid-template-columns: 1fr;

    .ant-form-item {
      grid-column: 1 / -1;
    }
  }
`;

const StyledTextArea = styled(TextArea)`
  && {
    background: var(--white);
    border-radius: 6px;
    color: var(--text-primary);
  }
`;

const OccurredAtPicker = styled(DatePicker)`
  width: 100%;
`;

const ACTIVITY_TYPE_DROPDOWN_CLASS_NAME = 'shop-request-activity-type-dropdown';

const ActivityTypeDropdownStyles = createGlobalStyle`
  .${ACTIVITY_TYPE_DROPDOWN_CLASS_NAME}.ant-select-dropdown {
    border-radius: 0;
  }

  .${ACTIVITY_TYPE_DROPDOWN_CLASS_NAME} .ant-select-item-option {
    border-radius: 0;
  }

  .${ACTIVITY_TYPE_DROPDOWN_CLASS_NAME} .ant-select-item-option-content {
    display: flex;
    align-items: center;
  }

  .${ACTIVITY_TYPE_DROPDOWN_CLASS_NAME} .ant-select-item-option-disabled {
    color: var(--text-secondary);
    opacity: 0.55;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const StyledModal = styled(Modal)`
  &&& {
    .ant-modal-header {
      padding: 18px 24px 14px;
    }

    .ant-modal-footer {
      padding: 14px 20px;
    }
  }
`;

type Props = {
  communityId?: number;
  shopRequestId?: number;
  requestType?: ShopRequestType;
  isOpen: boolean;
  onCancel: () => void;
  onSaveSuccess: () => void | Promise<unknown>;
};

const getOutcomeOptions = (
  activityType: ShopRequestCommunicationLogActivityType,
  directionType: ShopRequestCommunicationLogDirectionType
) => {
  const outcomeOptionsByDirection = SHOP_REQUEST_COMMUNICATION_LOG_OUTCOME_OPTIONS_BY_ACTIVITY_TYPE_AND_DIRECTION[
    activityType
  ] as Partial<Record<ShopRequestCommunicationLogDirectionType, readonly { value: string; label: string }[]>>;

  return outcomeOptionsByDirection[directionType] ?? [];
};

const EditShopRequestCommunicationLogModal = ({
  communityId,
  shopRequestId,
  requestType,
  isOpen,
  onCancel,
  onSaveSuccess,
}: Props) => {
  const [form] = Form.useForm();
  const { showMessage } = useRootContext();
  const [isSaving, setIsSaving] = useState(false);
  const activityType = Form.useWatch('activity_type', form);
  const directionType = Form.useWatch('direction_type', form);
  const selectedActivityType = (activityType ??
    SHOP_REQUEST_COMMUNICATION_LOG_ACTIVITY_TYPE.PHONE_CALL) as ShopRequestCommunicationLogActivityType;
  const requiresWebFormShopperContact =
    requestType === SHOP_REQUEST_TYPE.SPEED_TO_LEAD || requestType === SHOP_REQUEST_TYPE.SALES_EXPERIENCE;
  const { data: shopperContacts = [] } = useQuery<ShopRequestShopperEmailType[]>({
    queryKey: ['shop-request', shopRequestId, 'shopper-emails'],
    queryFn: () => getShopRequestShopperEmails(shopRequestId as number),
    enabled: isOpen && Boolean(shopRequestId) && requiresWebFormShopperContact,
    refetchOnWindowFocus: false,
  });
  const hasCompleteShopperContact = shopperContacts.some((shopperContact) =>
    Boolean(shopperContact.email?.trim() && shopperContact.phone?.trim())
  );
  const webFormOptionDisabled = requiresWebFormShopperContact && !hasCompleteShopperContact;
  const activityTypeOptions = SHOP_REQUEST_COMMUNICATION_LOG_ACTIVITY_TYPE_OPTIONS.map((option) => ({
    ...option,
    disabled: option.value === SHOP_REQUEST_COMMUNICATION_LOG_ACTIVITY_TYPE.WEB_FORM_SUBMITTED && webFormOptionDisabled,
  }));
  const directionOptions = SHOP_REQUEST_COMMUNICATION_LOG_DIRECTION_OPTIONS_BY_ACTIVITY_TYPE[selectedActivityType];
  const selectedDirectionType = (directionType ??
    directionOptions[0].value) as ShopRequestCommunicationLogDirectionType;
  const outcomeOptions = getOutcomeOptions(selectedActivityType, selectedDirectionType);
  const disableDirection = directionOptions.length === 1;
  const disableOutcome = outcomeOptions.length === 1;

  const resetForm = () => {
    form.resetFields();
  };

  const closeModal = () => {
    if (isSaving) {
      return;
    }

    resetForm();
    onCancel();
  };

  const saveLogEntry = async (values: any) => {
    if (!communityId || !shopRequestId || isSaving) {
      return;
    }

    const oversizedFile = getOversizedUploadFile(values.files);
    if (oversizedFile) {
      showMessage('error', getFileSizeLimitErrorMessage(oversizedFile.name));
      return;
    }

    setIsSaving(true);
    try {
      const filePayload = await buildCommunicationLogFilePayload(values.files);

      await createShopRequestCommunicationLog(communityId, {
        shop_request_id: shopRequestId,
        activity_type: values.activity_type,
        direction_type: values.direction_type,
        outcome: values.outcome,
        description: values.description?.trim() ?? '',
        occurred_at:
          values.activity_type === SHOP_REQUEST_COMMUNICATION_LOG_ACTIVITY_TYPE.WEB_FORM_SUBMITTED && values.occurred_at
            ? dayjs(values.occurred_at).toISOString()
            : undefined,
        files: filePayload,
      });
      await onSaveSuccess();
      showMessage('success', 'Communication log entry saved.');
      resetForm();
      onCancel();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.shop_request_id?.[0] ||
        error?.response?.data?.detail ||
        'Failed to save communication log entry.';
      showMessage('error', errorMessage);
      console.error('Failed to save communication log entry.', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleValuesChange = (changedValues: any) => {
    if (changedValues.activity_type) {
      const changedActivityType = changedValues.activity_type as ShopRequestCommunicationLogActivityType;
      const selectedDirectionOptions =
        SHOP_REQUEST_COMMUNICATION_LOG_DIRECTION_OPTIONS_BY_ACTIVITY_TYPE[changedActivityType];
      const nextDirectionType = selectedDirectionOptions[0].value;
      const selectedOutcomeOptions = getOutcomeOptions(changedActivityType, nextDirectionType);

      form.setFieldsValue({
        direction_type: nextDirectionType,
        outcome: selectedOutcomeOptions[0].value,
      });
      if (changedActivityType === SHOP_REQUEST_COMMUNICATION_LOG_ACTIVITY_TYPE.WEB_FORM_SUBMITTED) {
        form.setFieldValue('occurred_at', dayjs());
      }
      return;
    }

    if (changedValues.direction_type) {
      const changedDirectionType = changedValues.direction_type as ShopRequestCommunicationLogDirectionType;
      const selectedOutcomeOptions = getOutcomeOptions(selectedActivityType, changedDirectionType);

      form.setFieldValue('outcome', selectedOutcomeOptions[0].value);
    }
  };

  const beforeUpload = (file: File) => {
    if (!isFileWithinSizeLimit(file)) {
      showMessage('error', getFileSizeLimitErrorMessage(file.name));
      return Upload.LIST_IGNORE;
    }

    return false;
  };

  return (
    <StyledModal
      title="New Log Entry"
      open={isOpen}
      onCancel={closeModal}
      width="fit-content"
      footer={
        <Footer>
          <Button disabled={isSaving} onClick={closeModal}>
            Cancel
          </Button>
          <Button type="primary" loading={isSaving} disabled={!communityId || !shopRequestId} onClick={form.submit}>
            Save
          </Button>
        </Footer>
      }
    >
      <ActivityTypeDropdownStyles />
      <Form
        form={form}
        layout="vertical"
        initialValues={DEFAULT_FORM_VALUES}
        onValuesChange={handleValuesChange}
        onFinish={saveLogEntry}
      >
        <FormGrid>
          <Form.Item label="Type" name="activity_type">
            <Select
              className="rectangular"
              popupClassName={ACTIVITY_TYPE_DROPDOWN_CLASS_NAME}
              options={activityTypeOptions}
            />
          </Form.Item>
          <Form.Item label="Direction" name="direction_type">
            <Select className="rectangular" disabled={disableDirection} options={[...directionOptions]} />
          </Form.Item>
          <Form.Item label="Outcome" name="outcome">
            <Select className="rectangular" disabled={disableOutcome} options={[...outcomeOptions]} />
          </Form.Item>
          {selectedActivityType === SHOP_REQUEST_COMMUNICATION_LOG_ACTIVITY_TYPE.WEB_FORM_SUBMITTED && (
            <Form.Item
              label="Form submitted at"
              name="occurred_at"
              rules={[{ required: true, message: 'Enter the time the form was submitted.' }]}
            >
              <OccurredAtPicker showTime format="MMM D, YYYY h:mm A" />
            </Form.Item>
          )}
          <Form.Item className="full-width" label="Description" name="description">
            <StyledTextArea placeholder="Describe what happened..." rows={4} />
          </Form.Item>
          <EditShopRequestCommunicationLogUploadField beforeUpload={beforeUpload} />
        </FormGrid>
      </Form>
    </StyledModal>
  );
};

export default EditShopRequestCommunicationLogModal;
